<script setup lang="ts">
import AlarmResource from '@/api/resources/alarm'
import SensorResource from '@/api/resources/sensor'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import AlarmClassificationBlock from '@/components/Sensors/AlarmClassificationBlock.vue'
import AlarmDetailChart from '@/components/Sensors/AlarmDetailChart.vue'
import AlarmSetpointsBlock from '@/components/Sensors/AlarmSetpointsBlock.vue'
import { getSensorColor, type SensorType } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useAlarmStore } from '@/store/alarm'
import { useUserStore } from '@/store/user'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const alarmStore = useAlarmStore()
const userStore = useUserStore()

/** Mapa alarm_type (API) -> label exibido */
const ALARM_TYPE_LABELS: Record<string, string> = {
  interruption: 'Interrupção',
  low_load: 'Carga baixa',
  power_factor: 'Fator de potência',
  activation_peak_hour: 'Pico de ativação',
  empty_mixer: 'Misturador vazio',
  outside_regular_time: 'Fora do horário',
  burnt_resistor: 'Resistor queimado',
  defrost_time: 'Tempo de degelo',
  no_data: 'Sem dados',
  critical_temperature: 'Temperatura crítica',
  door_open: 'Porta aberta',
  overvoltage: 'Sobretensão',
  correlational: 'Correlacional'
}

/** Estado passado pela navegação ou montado a partir da API (acesso direto por URL) */
interface AlarmDetailState {
  alarm: {
    id: number
    start_date: string
    end_date: string | null
    alarm_type?: string
    classification_type?: { id: number; name: string; nature?: string | null } | null
    classified_by?: string | null
    created_at?: string
    updated_at?: string
  }
  sensorId: number
  alarmType: string
  alarmTypeLabel: string
  sensorType: 'electricity' | 'temperature' | 'door_open'
}

const state = ref<AlarmDetailState | null>(null)
const isLoadingAlarm = ref(false)
const loadError = ref(false)

function initState() {
  const fromHistory = (history.state as { alarmDetail?: AlarmDetailState })?.alarmDetail
  if (fromHistory) {
    state.value = fromHistory
    return
  }
  state.value = (route.meta?.alarmDetailState as AlarmDetailState) ?? null
}

const alarmIdParam = computed(() => Number(route.params.alarmId) || 0)
const sensorId = computed(() => Number(route.params.sensorId) || state.value?.sensorId || 0)
initState()
const alarm = computed(() => state.value?.alarm ?? null)
const alarmTypeLabel = computed(() => state.value?.alarmTypeLabel ?? 'Alarme')
const sensorType = computed(() => state.value?.sensorType ?? 'electricity')
const routeAlarmType = computed(() => String(route.query.alarmType ?? ''))

function sensorTypeColorClass(type: SensorType | string): string {
  const color = getSensorColor(type as SensorType)
  return color.startsWith('#') ? '' : color
}
function sensorTypeColorStyle(type: SensorType | string): Record<string, string> {
  const color = getSensorColor(type as SensorType)
  return color.startsWith('#') ? { backgroundColor: color } : {}
}

/** Admin ou manager podem editar a classificação depois de atribuída */
const canEditClassification = computed(() => {
  const role = userStore.getCurrentUser?.role ?? userStore.getCurrentRole
  if (!role) return false
  const roleStr = String(role).toLowerCase()
  return roleStr === 'admin' || roleStr === 'manager'
})

async function loadAlarmById() {
  const id = alarmIdParam.value
  if (!id) return
  isLoadingAlarm.value = true
  try {
    const data = await AlarmResource.getAlarm(id)
    const sensor = data?.sensor
    const sensorIdFromAlarm = sensor?.id
    const sensorTypeFromAlarm = sensor?.sensor_type
    const st =
      sensorTypeFromAlarm === 'temperature'
        ? 'temperature'
        : sensorTypeFromAlarm === 'door_open'
          ? 'door_open'
          : 'electricity'
    state.value = {
      alarm: {
        id: data.id,
        start_date: data.start_date,
        end_date: data.end_date ?? null,
        alarm_type: data.alarm_type,
        classification_type: data.classification_type ?? null,
        classified_by: data.classified_by ?? null,
        created_at: data.created_at,
        updated_at: data.updated_at,
      },
      sensorId: sensorIdFromAlarm ?? sensorId.value,
      alarmType: data.alarm_type ?? routeAlarmType.value ?? '',
      alarmTypeLabel:
        ALARM_TYPE_LABELS[data.alarm_type] ?? data.alarm_type ?? routeAlarmType.value ?? 'Alarme',
      sensorType: st,
    }
  } catch {
    state.value = null
    loadError.value = true
  } finally {
    isLoadingAlarm.value = false
  }
}

const sensorName = ref<string>('')
const classifications = ref<Array<{ id: number; name: string; nature?: string | null }>>([])
const classificationBlockRef = ref<InstanceType<typeof AlarmClassificationBlock> | null>(null)

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(startStr: string | null, endStr: string | null): string {
  if (!startStr || !endStr) return 'Em aberto'
  const start = new Date(startStr).getTime()
  const end = new Date(endStr).getTime()
  const diffMs = Math.max(0, end - start)
  const totalMin = Math.floor(diffMs / 60000)
  const d = Math.floor(totalMin / 1440)
  const restMin = totalMin % 1440
  const h = Math.floor(restMin / 60)
  const m = restMin % 60
  if (d > 0) return `${d}d ${h}h ${m} min`
  if (h > 0) return `${h}h ${m} min`
  return `${totalMin} min`
}

function goBack() {
      router.back()

}

function goToSensorDetail() {
  if (sensorId.value) {
    router.push({ name: 'sensor-detail', params: { id: sensorId.value } })
  }
}

async function loadSensorName() {
  if (!sensorId.value) return
  try {
    if (sensorType.value === 'temperature') {
      const s = await SensorResource.getTemperatureSensorsById(sensorId.value)
      sensorName.value = s?.name ?? ''
    } else if (sensorType.value === 'door_open') {
      const s = await SensorResource.getOpenSensorsById(sensorId.value)
      sensorName.value = s?.name ?? ''
    } else {
      const s = await SensorResource.getEnergySensorsById(sensorId.value)
      sensorName.value = s?.name ?? ''
    }
  } catch {
    sensorName.value = ''
  }
}

async function loadClassifications() {
  await alarmStore.fetchAlarmClassifications({ force: true })
  classifications.value =
    alarmStore.listClassifications as Array<{ id: number; name: string; nature?: string | null }>
}

async function onClassificationSubmit(payload: { classificationId: number; endAlarm: boolean }) {
  if (!alarm.value) return
  try {
    const updated = await alarmStore.editAlarm(alarm.value.id, payload.classificationId, payload.endAlarm)
    classificationBlockRef.value?.setResult(true, 'Alarme classificado com sucesso.')
    if (state.value?.alarm && updated) {
      state.value.alarm.classification_type = updated.classification_type ?? state.value.alarm.classification_type
      state.value.alarm.classified_by = (updated as any).classified_by
      state.value.alarm.updated_at = (updated as any).updated_at
      if (updated.end_date) {
        state.value.alarm.end_date = updated.end_date
      }
    }
  } catch {
    classificationBlockRef.value?.setResult(false, 'Não foi possível classificar o alarme. Tente novamente.')
  }
}

function onClassified(classification: { id: number; name: string; nature?: string | null }) {
  if (state.value?.alarm) {
    state.value.alarm.classification_type = classification
  }
}

onMounted(async () => {
  initState()
  const id = alarmIdParam.value
  if (!state.value && id) {
    await loadAlarmById()
  }
  if (loadError.value) return
  if (!alarm.value || !sensorId.value) {
    goBack()
    return
  }
  loadSensorName()
  loadClassifications()
})
</script>

<template>
  <DefaultLayout>
    <template v-if="loadError">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <p class="text-lg font-semibold text-gray-700 mb-2">Alarme nao encontrado</p>
        <p class="text-sm text-gray-500 mb-4">O alarme pode pertencer a outra empresa ou nao existe mais.</p>
        <button @click="router.push({ name: 'dashboard' })" class="px-4 py-2 bg-blue text-white rounded hover:bg-blue-2 text-sm">
          Voltar ao Dashboard
        </button>
      </div>
    </template>
    <template v-else-if="isLoadingAlarm || !alarm || !sensorId">
      <Loader />
    </template>

    <template v-else>
      <div class="mx-auto max-w-[98%] px-2 py-4">
        <!-- Cabeçalho: voltar + título à esquerda; tipo do alarme alinhado à direita -->
        <div class="mb-4 border border-slate-200 bg-white p-6 shadow-sm">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex flex-wrap items-center gap-3">
              <button
                type="button"
                class="flex items-center justify-center border border-slate-300 bg-white p-2 text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-800"
                title="Voltar ao sensor"
                @click="goBack">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 class="text-xl font-bold text-slate-900">Detalhe do alarme</h1>
              </div>
            </div>
            <div class="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                class=" px-3 py-1 text-sm font-medium text-white shadow-sm ring-1 ring-black/10 transition-opacity hover:opacity-90"
                :class="sensorTypeColorClass(sensorType)"
                :style="sensorTypeColorStyle(sensorType)"
                title="Ver detalhes do sensor"
                @click="goToSensorDetail">
                {{ sensorName || `Sensor #${sensorId}` }}
              </button>
              <span class="border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-red-800">
                {{ alarmTypeLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Bloco: Informações do alarme (início, fim, duração) -->
        <div class="mb-6 border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h2 class="text-base font-semibold text-slate-800">Informações do alarme</h2>
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Início</p>
              <p class="mt-1 font-medium tabular-nums text-slate-900">{{ formatDateTime(alarm.start_date) }}</p>
            </div>
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Fim</p>
              <p class="mt-1 font-medium tabular-nums text-slate-900">{{ alarm.end_date ? formatDateTime(alarm.end_date) : 'Em aberto' }}</p>
            </div>
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Duração</p>
              <p class="mt-1 font-medium text-slate-900">{{ formatDuration(alarm.start_date, alarm.end_date) }}</p>
            </div>
          </div>
        </div>

        <!-- Bloco de classificação: logo após informações do alarme (admin/manager podem editar) -->
        <div class="mb-6">
          <AlarmClassificationBlock
            ref="classificationBlockRef"
            :alarm="alarm"
            :classifications="classifications"
            :can-edit-classification="canEditClassification"
            @submit="onClassificationSubmit"
            @classified="onClassified"
          />
        </div>

        <!-- Gráfico com região do alarme (zoom e pan ativos) -->
        <div class="mb-6">
          <AlarmDetailChart
            :sensor-id="sensorId"
            :sensor-type="sensorType"
            :alarm-type="alarm.alarm_type ?? ''"
            :start-date="alarm.start_date"
            :end-date="alarm.end_date"
            :alarm-id="alarm.id"
          />
        </div>

        <!-- Setpoints usados no sensor para este tipo de alarme (no fim da página) -->
        <div class="mb-6">
          <AlarmSetpointsBlock
            :sensor-id="sensorId"
            :sensor-type="sensorType"
            :alarm-type="alarm.alarm_type ?? ''"
          />
        </div>
      </div>
    </template>
  </DefaultLayout>
</template>
